import * as signalR from '@microsoft/signalr';
import { Message } from '../types/message';

const SIGNALR_URL = process.env.REACT_APP_SIGNALR_URL || 'http://localhost:5231/hubs/chat';

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private messageCallbacks: Set<(message: Message) => void> = new Set();
  private channelMessageCallbacks: Set<(message: Message) => void> = new Set();

  async connect(token: string): Promise<void> {
    // Disconnect existing connection first
    if (this.connection) {
      if (this.connection.state === signalR.HubConnectionState.Connected ||
          this.connection.state === signalR.HubConnectionState.Connecting ||
          this.connection.state === signalR.HubConnectionState.Reconnecting) {
        await this.connection.stop();
      }
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(SIGNALR_URL, {
        accessTokenFactory: () => token,
        transport: signalR.HttpTransportType.WebSockets |
                  signalR.HttpTransportType.ServerSentEvents |
                  signalR.HttpTransportType.LongPolling,
        skipNegotiation: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Register event handlers
    this.connection.on('NewMessage', (message: Message) => {
      this.messageCallbacks.forEach((callback) => callback(message));
    });

    this.connection.on('NewChannelMessage', (message: Message) => {
      this.channelMessageCallbacks.forEach((callback) => callback(message));
    });

    this.connection.onreconnecting(() => {
      console.log('SignalR reconnecting...');
    });

    this.connection.onreconnected(() => {
      console.log('SignalR reconnected');
    });

    this.connection.onclose(() => {
      console.log('SignalR connection closed');
    });

    try {
      await this.connection.start();
      console.log('SignalR connected');
    } catch (err) {
      console.error('SignalR connection error:', err);
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  onMessage(callback: (message: Message) => void): () => void {
    this.messageCallbacks.add(callback);
    return () => this.messageCallbacks.delete(callback);
  }

  onChannelMessage(callback: (message: Message) => void): () => void {
    this.channelMessageCallbacks.add(callback);
    return () => this.channelMessageCallbacks.delete(callback);
  }

  async joinChannel(channelId: number): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('Not connected to SignalR hub');
    }

    await this.connection.invoke('JoinChannel', channelId);
  }

  async leaveChannel(channelId: number): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('Not connected to SignalR hub');
    }

    await this.connection.invoke('LeaveChannel', channelId);
  }

  async markAsRead(messageId: number): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('Not connected to SignalR hub');
    }

    await this.connection.invoke('MarkMessageAsRead', messageId);
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

export const signalRService = new SignalRService();
